// src/utils/actionMaterials.ts
/**
 * Materiales de una actuación en la tabla — resolución de NOMBRES.
 *
 * El backend desnormaliza en `Action.materialIds` solo los IDS. Los nombres se
 * resuelven aquí contra el catálogo que ya está cacheado
 * (`gql/services/materialsCatalog.ts`), y esa decisión es deliberada:
 *
 * 🔴 Si el nombre viajara desnormalizado en la Actuación, renombrar un material
 * en Gestión dejaría OBSOLETAS todas las filas guardadas — la misma deriva por
 * emparejamiento-por-copia que ya duele en `isLaneCut`. Resolviendo contra el
 * catálogo, el nombre está siempre al día y el offline sale gratis: el catálogo
 * se precachea y `fetchMaterialsCatalog` no sale a la red en la primera lectura.
 *
 * De paso mata el N+1 doble que hacía el selector: dos queries (MaterialUnit +
 * Material) por CADA material de CADA fila.
 */

import type { MaterialCatalogEntry } from '../gql/services/materialsCatalog';

/** Lo que la celda necesita saber de una fila. */
export type ActionMaterialsSource = {
  materialIds?: (string | null)[] | null;
};

/**
 * ¿Hace falta bajar el catálogo para este listado?
 *
 * Solo si alguna fila tiene materiales que pintar. Un listado entero sin
 * materiales —o uno servido por un backend viejo que no manda el campo— se
 * ahorra la consulta, igual que hace `needsLaneCutFallback`.
 */
export const needsMaterialsCatalog = (
  actions: (ActionMaterialsSource | null | undefined)[]
): boolean => actions.some((action) => (action?.materialIds?.length ?? 0) > 0);

/** Índice id → nombre, para resolver en O(1) por fila. */
export const buildMaterialNameMap = (
  catalog: MaterialCatalogEntry[]
): Record<string, string> =>
  catalog.reduce<Record<string, string>>((map, material) => {
    if (material?.id && material?.name) map[material.id] = material.name;
    return map;
  }, {});

/**
 * Nombres de los materiales de una fila, en el orden en que vienen.
 *
 * Fail-soft por diseño: un id que no está en el catálogo se OMITE en vez de
 * pintar el ObjectId crudo — sin cobertura y con la caché fría el catálogo
 * llega vacío, y es mejor una celda vacía que una fila llena de ids. La celda
 * decide qué enseñar cuando esto devuelve nada.
 */
export const resolveMaterialNames = (
  materialIds: (string | null)[] | null | undefined,
  nameById: Record<string, string>
): string[] =>
  (materialIds ?? [])
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .map((id) => nameById[id])
    .filter((name): name is string => Boolean(name));

/** Separador de los ids que comparten un mismo nombre dentro de un `value`. */
export const MATERIAL_GROUP_SEPARATOR = ',';

/**
 * Opciones del multi-select: UNA por NOMBRE, sin código a la vista.
 *
 * 🔴 Decisión de producto: en materiales no se enseña el identificador. El
 * operario busca y reconoce por el nombre, que es lo que ve en la actuación y
 * en el informe; el código es interno.
 *
 * Eso obliga a agrupar. El catálogo tiene nombres duplicados con ids distintos
 * —medido en beta: «Aglomerat en fred en pots de 25 Kg» M00001/M00002 (10 y 24
 * usos) y «Pastilla de goma» M00003/M00004 (14 y 9)—, así que sin el código y
 * sin agrupar saldrían DOS filas idénticas que filtran cosas distintas, que es
 * lo peor de los dos mundos. Agrupadas, elegir ese nombre trae las actuaciones
 * de todas sus entradas (34 y 23 en los casos de arriba), que es justo lo que
 * significa el nombre para quien lo busca.
 *
 * No se pierde nada: el `value` lleva TODOS los ids del grupo, así que el
 * filtro sigue consultando por ids reales.
 */
export const buildMaterialFilterOptions = (
  materials: { id: string; name: string; materialId?: string }[]
): { label: string; value: string }[] => {
  const byName = new Map<string, { label: string; ids: string[] }>();

  for (const material of materials) {
    if (!material?.id) continue;
    const label = (material.name ?? '').trim();
    // La clave normaliza para que «Biones» y «biones » no salgan dos veces;
    // la etiqueta conserva la primera grafía del catálogo.
    const key = label.toLocaleLowerCase();
    const group = byName.get(key);
    if (group) group.ids.push(material.id);
    else byName.set(key, { label, ids: [material.id] });
  }

  return [...byName.values()]
    .map((group) => ({
      label: group.label,
      value: [...group.ids].sort().join(MATERIAL_GROUP_SEPARATOR),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Del `value` de las opciones marcadas a los ids que viajan en el filtro.
 * Un grupo puede traer varios ids; el backend los recibe planos.
 */
export const expandMaterialFilterValues = (values: string[]): string[] =>
  [...new Set(values.flatMap((v) => v.split(MATERIAL_GROUP_SEPARATOR)))].filter(
    Boolean
  );

/**
 * El camino de vuelta: qué opciones hay que pintar marcadas dado lo que el
 * cursor guarda (ids planos).
 *
 * Basta con que coincida UNO de los ids del grupo: así un cursor guardado
 * antes de la agrupación —con un solo id— sigue saliendo marcado en vez de
 * perderse en silencio.
 */
export const collapseMaterialIdsToValues = (
  materialIds: string[] | undefined,
  options: { value: string }[]
): string[] => {
  if (!materialIds?.length) return [];
  const selected = new Set(materialIds);
  return options
    .filter((option) =>
      option.value
        .split(MATERIAL_GROUP_SEPARATOR)
        .some((id) => selected.has(id))
    )
    .map((option) => option.value);
};

// ─── Filtro «con / sin materiales» ──────────────────────────────────────────
//
// Los tres helpers del corte de carril NO tienen nada de corte de carril: son
// el conversor genérico entre el valor de un desplegable Todos/Sí/No y el
// `boolean | undefined` del cursor, con las etiquetas ya traducidas. Se reusan
// tal cual —delegando, sin copiar la lógica— para no tener dos criterios que
// puedan divergir. Los alias existen para que el sitio de llamada se lea bien.
import {
  getLaneCutFilterOptions,
  laneCutSelectionToBoolean,
  booleanToLaneCutSelection,
} from './laneCut';

/** Opciones «Todos / Sí / No» del filtro de materiales. */
export const getHasMaterialsFilterOptions = getLaneCutFilterOptions;

/**
 * Del valor del desplegable al del filtro.
 *
 * 🔴 No se puede resolver con `value === 'true'`: cualquier otro valor daría
 * `false`, y eso filtraría por «sin materiales» en vez de no filtrar.
 */
export const hasMaterialsSelectionToBoolean = laneCutSelectionToBoolean;

/** El inverso, para pintar el estado del control desde el cursor. */
export const booleanToHasMaterialsSelection = booleanToLaneCutSelection;

// ─── Fila del selector de materiales del asistente ──────────────────────────

/** Lo que el catálogo trae y el asistente necesita conservar. */
type CatalogMaterial = {
  id: string;
  materialId: string;
  name: string;
  teamIDs?: string[];
  isActive?: boolean | null;
};

/** Nombres ya resueltos (cada loader los obtiene a su manera). */
type ResolvedNames = {
  typeOfMeasurement: string;
  codification: string;
  subCodification: string;
};

/**
 * Construye la fila del selector conservando los campos por los que se FILTRA.
 *
 * 🔴 Existe por un fallo real: los dos loaders del asistente reconstruían el
 * material campo a campo y arrastraban `teamIDs` pero NO `isActive`, así que
 * al filtro le llegaba `undefined` —que por la regla «ausente = activo»
 * significa ACTIVO— y un material dado de baja se seguía ofreciendo. En
 * silencio, porque `undefined` es un valor legítimo.
 *
 * Los tests del filtro no podían cazarlo: comprueban el criterio en
 * aislamiento, con objetos que sí traen el campo. El agujero estaba en el
 * camino, no en la regla. De ahí este punto único: quien añada mañana otro
 * campo de filtrado lo pone aquí y los dos asistentes lo heredan.
 */
export const buildWizardMaterialRow = <T extends CatalogMaterial>(
  material: T,
  names: ResolvedNames
) => ({
  id: material.id,
  materialId: material.materialId,
  name: material.name,
  typeOfMeasurement: names.typeOfMeasurement,
  codification: names.codification,
  subCodification: names.subCodification,
  // Campos de filtrado — la tabla de selección los ignora, el scope no.
  teamIDs: material.teamIDs ?? [],
  isActive: material.isActive ?? null,
});
